// Trigger for the global Customer Assist widget. Used in both:
//   - The ticket detail header (variant='header')
//   - The right-rail TicketActions cluster (variant='default')
//
// Just a dispatcher into AssistSessionProvider — the actual session
// state + channel subscription live there and persist across
// navigation. Clicking this button either expands the existing widget
// (if a session is in flight for this ticket) or opens the setup
// modal bound to this ticket.
//
// Active-state cue (amber → emerald moved here last session, kept):
// when `useActiveAssistSession(ticketId)` resolves to a row, the
// button reads "Live session open" with an emerald tint so the agent
// knows clicking just resumes the existing chat rather than starting
// a fresh one.

import { MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useActiveAssistSession } from '@/lib/queries';
import { useAssistSession } from '@/lib/assist-session-context';
import { cn } from '@/lib/utils';

interface Props {
  ticketId: string;
  /** Ticket subject — shown in the widget's pill + header so the agent
   *  knows which session they're in when juggling multiple. */
  ticketSubject?: string;
  /** Visual treatment. `default` is the right-rail look (full-size
   *  outline button). `header` is the compact treatment alongside the
   *  ticket header badges (h-7 / text-xs). */
  variant?: 'default' | 'header' | 'toolbar';
  /** Forwarded to the underlying Button. Caller uses this to gate
   *  clicks while sibling actions are running. */
  disabled?: boolean;
}

export function CustomerAssistButton({ ticketId, ticketSubject, variant = 'default', disabled }: Props) {
  const session = useAssistSession();
  const activeQ = useActiveAssistSession(ticketId);
  const active = !!activeQ.data;

  const label = active ? 'Live session open' : 'Live assist';
  const title = active
    ? 'A live session is already in flight on this ticket — click to rejoin'
    : 'Open a live chat with the client for a time-sensitive moment';

  function handleClick() {
    void session.openForTicket({ ticketId, ticketSubject });
  }

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={handleClick}
      disabled={disabled}
      title={title}
      aria-label={label}
      className={cn(
        // Active state — filled emerald with white text reads as a
        // "live now" indicator from across the page. The pulse dot
        // adds motion so it catches the eye even when scrolled away
        // from the main header. Previous soft-emerald outline was
        // too subtle to notice while juggling tickets.
        active &&
          'border-tint-success-fg bg-tint-success-fg text-background shadow-sm hover:border-tint-success-fg hover:bg-tint-success-fg hover:text-background',
        variant === 'header' && 'h-7 px-2 text-xs',
        // Pinned ticket-actions bar: icon-only square below md.
        variant === 'toolbar' && 'max-md:w-9 max-md:px-0',
      )}
    >
      {active ? (
        <span
          className={cn(
            'relative flex h-2 w-2 shrink-0',
            variant === 'toolbar' ? 'md:mr-1.5' : 'mr-1.5',
          )}
        >
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-70" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
        </span>
      ) : (
        <MessageSquare
          className={cn(
            variant === 'toolbar' ? 'md:mr-1' : 'mr-1',
            variant === 'header' ? 'h-3.5 w-3.5' : 'h-4 w-4',
          )}
        />
      )}
      <span className={variant === 'toolbar' ? 'hidden md:inline' : undefined}>{label}</span>
    </Button>
  );
}
